import { useState, useEffect } from "react";
import ConfigAPI from "../../services/ConfigAPI";

export default function IgnoredStatusSection() {
    const [ignoredStatus, setIgnoredStatus] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [formData, setFormData] = useState({
        displayName: '',
        searchTerms: [''],
        matchType: 'contains',
        comment: ''
    });

    useEffect(() => {
        loadIgnoredStatus();
    }, []);

    const loadIgnoredStatus = async () => {
        try {
            setLoading(true);
            const response = await ConfigAPI.getIgnoredStatus();
            if (response.success) {
                setIgnoredStatus(response.data);
            }
        } catch (err) {
            console.error("Erro ao carregar ignored status:", err);
            setError("Erro ao carregar status ignorados");
        } finally {
            setLoading(false);
        }
    };

    const handleAddSearchTerm = () => {
        setFormData(prev => ({
            ...prev,
            searchTerms: [...prev.searchTerms, '']
        }));
    };

    const handleRemoveSearchTerm = (index) => {
        setFormData(prev => ({
            ...prev,
            searchTerms: prev.searchTerms.filter((_, i) => i !== index)
        }));
    };

    const handleSearchTermChange = (index, value) => {
        setFormData(prev => ({
            ...prev,
            searchTerms: prev.searchTerms.map((term, i) => i === index ? value : term)
        }));
    };

    const handleEdit = (status) => {
        setEditingStatus(status);
        setFormData({
            displayName: status.displayName,
            searchTerms: [...status.searchTerms],
            matchType: status.matchType,
            comment: status.comment
        });
        setShowAddModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir este status ignorado?')) {
            return;
        }

        try {
            setLoading(true);
            const response = await ConfigAPI.deleteIgnoredStatus(id);
            
            if (response.success) {
                setSuccess("Status ignorado deletado com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                await loadIgnoredStatus();
            }
        } catch (err) {
            console.error("Erro ao deletar ignored status:", err);
            setError(err.response?.data?.message || "Erro ao deletar status ignorado");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        const validSearchTerms = formData.searchTerms.filter(term => term.trim() !== '');
        
        if (!formData.displayName.trim()) {
            alert('O nome de exibição é obrigatório');
            return;
        }

        if (validSearchTerms.length === 0) {
            alert('Adicione pelo menos um termo de busca');
            return;
        }

        try {
            setLoading(true);
            
            const data = {
                displayName: formData.displayName,
                searchTerms: validSearchTerms,
                matchType: formData.matchType,
                comment: formData.comment
            };

            let response;
            
            if (editingStatus) {
                response = await ConfigAPI.updateIgnoredStatus(editingStatus._id, data);
            } else {
                response = await ConfigAPI.addIgnoredStatus(data);
            }

            if (response.success) {
                setSuccess(editingStatus ? "Status ignorado atualizado com sucesso" : "Status ignorado criado com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                resetForm();
                setShowAddModal(false);
                await loadIgnoredStatus();
            }
        } catch (err) {
            console.error("Erro ao salvar ignored status:", err);
            setError(err.response?.data?.message || "Erro ao salvar status ignorado");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            displayName: '',
            searchTerms: [''],
            matchType: 'contains',
            comment: ''
        });
        setEditingStatus(null);
    };

    const handleResetToDefaults = async () => {
        if (!window.confirm('Deseja limpar todos os status ignorados?')) {
            return;
        }

        try {
            setLoading(true);
            const response = await ConfigAPI.resetIgnoredStatus();
            
            if (response.success) {
                setSuccess("Status ignorados limpos com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                await loadIgnoredStatus();
            }
        } catch (err) {
            console.error("Erro ao resetar ignored status:", err);
            setError(err.response?.data?.message || "Erro ao resetar status ignorados");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="section-content">
            <h1>Status Ignorados</h1>
            <p className="card-description">
                Configure quais status devem ser ignorados e retornar "Nenhum ticket foi localizado"
            </p>

            {success && (
                <div className="alert-success">
                    <span>✓ {success}</span>
                </div>
            )}

            {error && (
                <div style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontWeight: '500'
                }}>
                    {error}
                </div>
            )}

            <div className="config-card">
                <div className="card-header">
                    <h3>Status Ignorados ({ignoredStatus.length})</h3>
                    <div>
                        <button 
                            className="btn-add" 
                            onClick={() => {
                                resetForm();
                                setShowAddModal(true);
                            }}
                            disabled={loading}
                        >
                            + Novo Status Ignorado
                        </button>
                        <button 
                            className="btn-danger" 
                            onClick={handleResetToDefaults}
                            disabled={loading}
                        >
                            🗑️ Limpar Todos
                        </button>
                    </div>
                </div>

                {loading && ignoredStatus.length === 0 ? (
                    <div className="empty-state">
                        <p>Carregando...</p>
                    </div>
                ) : ignoredStatus.length === 0 ? (
                    <div className="empty-state">
                        <span className="empty-icon">🚫</span>
                        <p>Nenhum status ignorado configurado</p>
                        <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                            AWBs com status ignorados retornarão "Nenhum ticket foi localizado"
                        </p>
                    </div>
                ) : (
                    <div className="columns-list">
                        {ignoredStatus.map((status) => (
                            <div key={status._id} className="column-item ignored-item">
                                <div className="ignored-indicator">🚫</div>
                                
                                <div className="column-main">
                                    <div className="column-header">
                                        <h4>{status.displayName}</h4>
                                        <div className="column-badges">
                                            <span className={`badge match-${status.matchType}`}>
                                                {status.matchType === 'exact' ? 'Texto Exato' : 'Contém'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="column-details">
                                        <div className="detail-group">
                                            <span className="detail-label">Termos de busca:</span>
                                            <div className="search-terms">
                                                {status.searchTerms.map((term, idx) => (
                                                    <span key={idx} className="search-term">
                                                        "{term}"
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {status.comment && (
                                            <div className="detail-group">
                                                <span className="detail-label">Comentário:</span>
                                                <p className="status-comment">{status.comment}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="column-actions">
                                    <button 
                                        className="btn-icon" 
                                        onClick={() => handleEdit(status)}
                                        disabled={loading}
                                        title="Editar"
                                    >
                                        ✏️
                                    </button>
                                    <button 
                                        className="btn-icon btn-danger-icon" 
                                        onClick={() => handleDelete(status._id)}
                                        disabled={loading}
                                        title="Deletar"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                }}>
                    <div className="modal-content mapping-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingStatus ? 'Editar Status Ignorado' : 'Novo Status Ignorado'}</h2>

                        <div className="form-group">
                            <label>Nome de Exibição *</label>
                            <input
                                type="text"
                                value={formData.displayName}
                                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                                placeholder="Ex: Cancelado, Extraviado, etc."
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label>Termos de Busca *</label>
                            <p className="helper-text">
                                AWBs com estes termos no subject retornarão "Nenhum ticket foi localizado"
                            </p>
                            {formData.searchTerms.map((term, index) => (
                                <div key={index} className="search-term-input">
                                    <input
                                        type="text"
                                        value={term}
                                        onChange={(e) => handleSearchTermChange(index, e.target.value)}
                                        placeholder='Ex: "cancelado", "extraviado", "perdido"'
                                        className="monospace"
                                        disabled={loading}
                                    />
                                    {formData.searchTerms.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn-remove"
                                            onClick={() => handleRemoveSearchTerm(index)}
                                            disabled={loading}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button 
                                type="button" 
                                className="btn-secondary small" 
                                onClick={handleAddSearchTerm}
                                disabled={loading}
                            >
                                + Adicionar Termo
                            </button>
                        </div>

                        <div className="form-group">
                            <label>Tipo de Correspondência</label>
                            <select
                                value={formData.matchType}
                                onChange={(e) => setFormData({ ...formData, matchType: e.target.value })}
                                disabled={loading}
                            >
                                <option value="contains">Contém (busca parcial)</option>
                                <option value="exact">Texto Exato</option>
                            </select>
                            <p className="helper-text">
                                {formData.matchType === 'contains' 
                                    ? 'Encontra o termo em qualquer parte do texto' 
                                    : 'O texto deve ser exatamente igual ao termo'}
                            </p>
                        </div>

                        <div className="form-group">
                            <label>Comentário (opcional)</label>
                            <textarea
                                value={formData.comment}
                                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                                placeholder="Adicione uma descrição sobre por que este status deve ser ignorado"
                                rows={3}
                                disabled={loading}
                            />
                        </div>

                        <div className="modal-actions">
                            <button 
                                className="btn-secondary" 
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-save" 
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : editingStatus ? 'Atualizar' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}